import { generatedApi } from './api.generated';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { DEX_COLD_WALLETS_API_ENDPOINTS as cfg } from './config';

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * Two complementary patterns are used here:
 *
 * 1. Per-project `ColdWallets` collection (LIST pattern).
 *    `listColdWallets` provides a per-item tag for every wallet plus a dedicated
 *    `{ type: 'ColdWallets', id: 'LIST' }` tag (the list response is wrapped —
 *    `result.data.list` is the wallet array). Linking / unlinking a wallet
 *    invalidates `{ id: 'LIST' }` to refetch the collection; updating a single
 *    wallet invalidates only that wallet's `{ id }` tag.
 *
 * 2. Scoped snapshot sub-collections (`listColdWalletSnapshots`,
 *    `listProjectSnapshots`). Each is scoped to a parent wallet / project, so the
 *    parent id is used as the discriminator under the `WalletSnapshots` tag type.
 *    `createColdWalletSnapshot` invalidates the affected wallets' scoped tags so their
 *    snapshot history refetches, leaving other wallets/projects untouched.
 */
const enhancedDexColdWalletApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['ColdWallets', 'WalletSnapshots'],
  endpoints: {
    // --- Per-project ColdWallets collection (LIST pattern) ---
    [cfg.listColdWallets.generated]: {
      providesTags: (result) => provideListTags(result?.data.list, 'ColdWallets'),
    },
    [cfg.linkColdWallet.generated]: {
      invalidatesTags: [{ type: 'ColdWallets', id: LIST_TAG_ID }],
    },
    [cfg.unlinkColdWallet.generated]: {
      invalidatesTags: [{ type: 'ColdWallets', id: LIST_TAG_ID }],
    },
    [cfg.updateColdWallet.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'ColdWallets', id: arg.walletId }],
    },

    // --- Scoped snapshot sub-collections ---
    [cfg.listColdWalletSnapshots.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'WalletSnapshots', id: arg.walletId }],
    },
    [cfg.listProjectSnapshots.generated]: {
      providesTags: (_result, _error, arg) => [
        { type: 'WalletSnapshots', id: `project-${arg.projectId}` },
      ],
    },
    [cfg.createColdWalletSnapshot.generated]: {
      // The generated `SnapshotDto` is an empty object in the spec, so the
      // affected wallet ids aren't reliably typed on the arg. Snapshotting is an
      // infrequent manual action, so every scoped snapshot collection is
      // invalidated to refetch freshly-captured history.
      invalidatesTags: ['WalletSnapshots'],
    },
  },
});

export const dexColdWalletApi: typeof enhancedDexColdWalletApi = enhancedDexColdWalletApi;

export const hookExports: HookExports<typeof cfg, typeof dexColdWalletApi> = createHookExports(
  cfg,
  dexColdWalletApi,
);

const endpointExports = createEndpointExports(cfg, dexColdWalletApi);

export const dexColdWalletApiEndpoints: typeof endpointExports = endpointExports;
